import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AlertTriangle, CalendarIcon, PlusCircle } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
    BillingFormData, NewAddressData, ReceiverInfo
} from '@/containers/logistic/purchaseOrders/wizard/NewPurchaseOrderWizard';
import { useBusinessConfigsQuery } from '@/hooks/business/useBusinessConfigsQuery';
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery';
import { useBusinessRegionsQuery } from '@/hooks/business/useBusinessRegionsQuery';
import { useCustomerAddressesQuery } from '@/hooks/business/useCustomerAddressesQuery';
import { useDeliveryPersonnelQuery } from '@/hooks/business/useDeliveryPersonnelQuery';
import { Country } from '@/interfaces/billing';
import { cn } from '@/lib/utils';

import { useMunicipalitiesList } from '../../clientManager/hooks/useMunicipalitiesList';
import { useProvincesList } from '../../clientManager/hooks/useProvincesList';
import { CountryPhoneSelector, CountrySelector } from './components';

interface DeliveryDetailsStepProps {
  formData: BillingFormData
  onNext: (data: Partial<BillingFormData>) => void
  setIsMainButtonDisabled: (disabled: boolean) => void
  onBack?: (data: Partial<BillingFormData>) => void
}

// Validation error interface
interface ValidationErrors {
  // Receiver details
  receiverName?: string
  receiverLastName?: string
  receiverPhone?: string

  // Address selection/creation
  addressSelection?: string
  newAddressMainAddress?: string
  newAddressCountry?: string
  newAddressProvince?: string
  newAddressMunicipality?: string

  // Shipping details
  deliveryPerson?: string
  deliveryDate?: string
  deliveryRegion?: string
  deliveryCost?: string
}

// Field name mapping for type-safe error clearing
const FIELD_TO_ERROR_MAP: Record<string, keyof ValidationErrors> = {
  // Receiver fields
  name: 'receiverName',
  lastName: 'receiverLastName',
  phone: 'receiverPhone',
  // Note: email field has no validation, so it's not mapped

  // New address fields
  mainAddress: 'newAddressMainAddress',
  country: 'newAddressCountry',
  province: 'newAddressProvince',
  municipality: 'newAddressMunicipality',
} as const

// Default state objects
const defaultReceiverInfo: ReceiverInfo = { name: '', lastName: '' }
const defaultNewAddressData: NewAddressData = {
  country: {
    id: '54',
    name: 'Cuba',
  },
}

// Helper function to compute initial receiver info
const getInitialReceiverInfo = (formData: BillingFormData): ReceiverInfo => {
  // If formData already has meaningful receiver info (returning to step), use it
  if (
    formData.receiverInfo &&
    (formData.receiverInfo.name?.trim() ||
      formData.receiverInfo.lastName?.trim())
  ) {
    return formData.receiverInfo
  }

  // If we have selected client data, prefill from it
  if (
    formData.clientId &&
    !formData.isNewClient &&
    formData.selectedClientData
  ) {
    const mainPhone = formData.selectedClientData.phones.find(
      (phone) => phone.isMain
    )

    return {
      name: formData.selectedClientData.firstName || '',
      lastName: formData.selectedClientData.lastName || '',
      phone: {
        number: mainPhone?.number || '',
        codeCountry: '+53',
      },
      email: formData.selectedClientData.email || '',
    }
  }

  // If it's a new client, prefill from newClientData
  if (formData.isNewClient && formData.newClientData) {
    return {
      name: formData.newClientData.name || '',
      lastName: formData.newClientData.lastName || '',
      phone: {
        number: formData.newClientData.phone?.number || '',
        codeCountry: formData.newClientData.phone?.codeCountry || '',
      },
      email: formData.newClientData.email || '',
    }
  }

  return defaultReceiverInfo
}

// Helper function to determine if new address form should be shown initially
const shouldShowNewAddressForm = (formData: BillingFormData): boolean => {
  // Show if explicitly requested in formData (returning to step with isNewAddress=true)
  if (formData.isNewAddress) return true

  // For new clients with pre-existing address data, show the form
  // (This will be overridden by the comprehensive effect, but provides initial state)
  if (
    formData.isNewClient &&
    formData.newAddressData &&
    Object.keys(formData.newAddressData).length > 0
  ) {
    return true
  }

  // Default to false - let the effects handle showing the form as needed
  return false
}

const DeliveryDetailsStep: FC<DeliveryDetailsStepProps> = ({
  formData,
  onNext,
  setIsMainButtonDisabled,
  onBack,
}) => {
  // Get business data and configs
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessConfigs, isLoadingBusinessConfigs, isErrorBusinessConfigs } =
    useBusinessConfigsQuery({
      params: {
        businessId: businessData?.id ?? 0,
      },
      options: {
        enabled:
          !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
      },
    })

  // Get delivery type config
  const deliveryTypeConfig = useMemo(
    () => businessConfigs?.find((config) => config.key === 'delivery_type'),
    [businessConfigs]
  )

  const deliveryType = useMemo(
    () =>
      deliveryTypeConfig?.value as
        | 'FIXED'
        | 'VARIABLE'
        | 'BYREGION'
        | undefined,
    [deliveryTypeConfig]
  )

  // Use new React Query hooks
  const {
    deliveryPersonnel,
    isLoadingDeliveryPersonnel,
    isErrorDeliveryPersonnel,
    errorDeliveryPersonnel,
  } = useDeliveryPersonnelQuery({
    businessId: businessData?.id ?? 0,
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  // Component state with proper initialization
  const [receiverInfo, setReceiverInfo] = useState<ReceiverInfo>(() =>
    getInitialReceiverInfo(formData)
  )

  const [selectedAddressId, setSelectedAddressId] = useState<
    string | undefined
  >(formData.deliveryAddressId)
  const [selectedAddressFullAddress, setSelectedAddressFullAddress] = useState<
    string | undefined
  >(formData.deliveryAddressFullAddress)

  const [showNewAddressForm, setShowNewAddressForm] = useState(() =>
    shouldShowNewAddressForm(formData)
  )
  const [newAddressData, setNewAddressData] = useState<NewAddressData>(
    formData.newAddressData || defaultNewAddressData
  )

  // Get country and province IDs with proper type handling - using local state
  const countryId = useMemo(() => {
    const id = newAddressData?.country?.id
    return typeof id === 'number'
      ? id
      : typeof id === 'string'
        ? parseInt(id) || 54
        : 54
  }, [newAddressData?.country?.id])

  const provinceId = useMemo(() => {
    const id = newAddressData?.province?.id
    return typeof id === 'number'
      ? id
      : typeof id === 'string'
        ? parseInt(id) || 0
        : 0
  }, [newAddressData?.province?.id])

  const {
    provinceOptions,
    isLoadingProvinces,
    isErrorProvinces,
    errorProvinces,
  } = useProvincesList({ countryId })

  const {
    municipalityOptions,
    isLoadingMunicipalities,
    isErrorMunicipalities,
    errorMunicipalities,
  } = useMunicipalitiesList({ provinceId })

  // Fetch customer addresses if client is selected
  const { addresses, isLoadingAddresses, isErrorAddresses, errorAddresses } =
    useCustomerAddressesQuery({
      businessId: businessData?.id ?? 0,
      customerId: formData.clientId ? parseInt(formData.clientId) : 0,
      params: { all_data: true },
      options: {
        enabled:
          !!formData.clientId &&
          !formData.isNewClient &&
          !!businessData?.id &&
          !isLoadingBusinessData &&
          !isErrorBusinessData,
      },
    })

  const [deliveryPersonId, setDeliveryPersonId] = useState(
    formData.deliveryPersonId || undefined
  )
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(
    formData.deliveryDate
  )
  const [deliveryCost, setDeliveryCost] = useState<string>(
    formData.deliveryCost?.toString() || ''
  )
  const [deliveryCostCurrency, setDeliveryCostCurrency] = useState(
    formData.deliveryCostCurrency || 'USD'
  )
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(
    formData.shippingRegionId
  )

  // Add immediate delivery state
  const [isImmediateDelivery, setIsImmediateDelivery] = useState<boolean>(
    formData.isImmediateDelivery || false
  )

  // Set delivery date to current time if immediate delivery was previously selected
  useEffect(() => {
    if (formData.isImmediateDelivery && !deliveryDate) {
      setDeliveryDate(new Date())
    }
  }, [formData.isImmediateDelivery])

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Improved validation error clearing with type safety and stale state protection
  const clearValidationError = (field: keyof ValidationErrors) => {
    setValidationErrors((prevErrors) => {
      // Check if the error actually exists before clearing
      if (!prevErrors[field]) {
        return prevErrors // No change needed
      }

      // Return new state with the specific field cleared
      return {
        ...prevErrors,
        [field]: undefined,
      }
    })
  }

  // Safe field error clearing using field mapping
  const clearFieldErrorByName = (fieldName: string) => {
    const errorKey = FIELD_TO_ERROR_MAP[fieldName]

    if (errorKey) {
      clearValidationError(errorKey)
    } else {
      // Log warning in development for unmapped fields
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `DeliveryDetailsStep: No error mapping found for field "${fieldName}"`
        )
      }
    }
  }

  // Check if there are any active validation errors
  const hasValidationErrors = useMemo(() => {
    return Object.values(validationErrors).some((error) => !!error)
  }, [validationErrors])

  // Update main button state whenever validation errors change
  useEffect(() => {
    setIsMainButtonDisabled(hasValidationErrors)
  }, [hasValidationErrors, setIsMainButtonDisabled])

  const validatePhone = (phone?: {
    number: string
    codeCountry: string
  }): boolean => {
    if (!phone?.number) return false
    const phoneRegex = /^\d{6,15}$/
    return phoneRegex.test(phone.number.replace(/\s+/g, ''))
  }

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {}
    let isValid = true

    // Receiver details validation
    if (!receiverInfo.name?.trim()) {
      errors.receiverName = 'El nombre del receptor es obligatorio'
      isValid = false
    }

    if (!receiverInfo.lastName?.trim()) {
      errors.receiverLastName = 'Los apellidos del receptor son obligatorios'
      isValid = false
    }

    if (!receiverInfo.phone?.number?.trim()) {
      errors.receiverPhone = 'El teléfono del receptor es obligatorio'
      isValid = false
    } else if (!validatePhone(receiverInfo.phone)) {
      errors.receiverPhone =
        'El teléfono debe contener solo números y tener entre 6 y 15 dígitos'
      isValid = false
    }

    // Address validation
    if (!selectedAddressId && !showNewAddressForm) {
      errors.addressSelection =
        'Debe seleccionar una dirección existente o crear una nueva'
      isValid = false
    }

    // New address validation (only if creating new address)
    if (showNewAddressForm) {
      if (!newAddressData.mainAddress?.trim()) {
        errors.newAddressMainAddress = 'La calle principal es obligatoria'
        isValid = false
      }

      if (!newAddressData.country?.name?.trim()) {
        errors.newAddressCountry = 'El país es obligatorio'
        isValid = false
      }

      if (!newAddressData.province?.id?.trim()) {
        errors.newAddressProvince = 'La provincia es obligatoria'
        isValid = false
      }

      if (!newAddressData.municipality?.id?.trim()) {
        errors.newAddressMunicipality = 'El municipio es obligatorio'
        isValid = false
      }
    }

    // Shipping details validation
    if (!deliveryPersonId?.trim()) {
      errors.deliveryPerson = 'Debe seleccionar un mensajero'
      isValid = false
    }

    // Only validate delivery date if not immediate delivery
    if (!isImmediateDelivery && !deliveryDate) {
      errors.deliveryDate = 'Debe seleccionar fecha y hora de entrega'
      isValid = false
    }

    // Delivery type specific validation
    if (deliveryType === 'BYREGION') {
      if (!selectedRegionId) {
        errors.deliveryRegion = 'Debe seleccionar una región de envío'
        isValid = false
      }
    } else if (deliveryType === 'VARIABLE') {
      // For variable delivery type, the field is optional
      // Only validate if a value is provided and it's invalid
      if (
        deliveryCost?.trim() &&
        (isNaN(parseFloat(deliveryCost)) || parseFloat(deliveryCost) < 0)
      ) {
        errors.deliveryCost = 'Debe especificar un precio de envío válido'
        isValid = false
      }
    }

    setValidationErrors(errors)
    setIsMainButtonDisabled(!isValid)
    return isValid
  }

  // Update receiver info when client selection changes (but preserve user edits)
  useEffect(() => {
    const isReceiverInfoEmpty =
      !receiverInfo.name &&
      !receiverInfo.lastName &&
      !receiverInfo.phone &&
      !receiverInfo.email

    // Only update if the fields are empty (preserve user edits)
    if (isReceiverInfoEmpty) {
      const newReceiverInfo = getInitialReceiverInfo(formData)
      setReceiverInfo(newReceiverInfo)
    }
  }, [formData.clientId, formData.isNewClient]) // Only depend on client changes

  // Handle switching between new client and existing client modes
  useEffect(() => {
    if (formData.isNewClient) {
      // For new clients, reset address selection and show new address form if address data exists
      setSelectedAddressId(undefined)
      setSelectedAddressFullAddress(undefined)
      if (
        formData.newAddressData &&
        Object.keys(formData.newAddressData).length > 0
      ) {
        setShowNewAddressForm(true)
        setNewAddressData(formData.newAddressData)
      } else {
        setShowNewAddressForm(false)
        setNewAddressData(defaultNewAddressData)
      }
    } else {
      // For existing clients, only reset if we're not returning with saved form state
      if (!formData.isNewAddress) {
        setShowNewAddressForm(false)
        setNewAddressData(defaultNewAddressData)
        // Only clear address selection if we don't have saved address data
        if (!formData.deliveryAddressId) {
          setSelectedAddressId(undefined)
          setSelectedAddressFullAddress(undefined)
        }
      }
    }
  }, [formData.isNewClient, formData.clientId])

  // Handle explicit new address form requests (when navigating back to step with isNewAddress=true)
  useEffect(() => {
    if (formData.isNewAddress && !showNewAddressForm) {
      setShowNewAddressForm(true)
      setSelectedAddressId(undefined)
      setSelectedAddressFullAddress(undefined)
      setNewAddressData(formData.newAddressData || defaultNewAddressData)
    }
  }, [formData.isNewAddress])

  // Handle address selection when addresses are loaded for existing clients
  useEffect(() => {
    // Only auto-select for existing clients with addresses, and not when returning with new address form state
    // Also don't auto-select if we already have a saved address selection
    if (
      !formData.isNewClient &&
      !formData.isNewAddress &&
      addresses.length > 0 &&
      !selectedAddressId &&
      !showNewAddressForm &&
      !formData.deliveryAddressId // Don't auto-select if we have saved address data
    ) {
      // Find main address or use first one
      const defaultAddress =
        addresses.find((addr) => addr.isMain) || addresses[0]
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id.toString())
        setSelectedAddressFullAddress(
          [
            defaultAddress?.street_1 || defaultAddress?.street_2,
            defaultAddress.locality,
            defaultAddress.municipality?.name,
            defaultAddress.province?.name,
            defaultAddress.country?.name,
          ]
            .filter(Boolean)
            .join(', ')
        )
      }
    }
  }, [
    addresses,
    formData.isNewClient,
    formData.isNewAddress,
    formData.deliveryAddressId,
  ])

  // Transform delivery personnel for select options
  const deliveryPeople = useMemo(() => {
    return deliveryPersonnel?.map((person) => ({
      id: person.id.toString(),
      name: person.displayName || person.username,
    }))
  }, [deliveryPersonnel])

  // Transform addresses for display
  const clientAddresses = useMemo(() => {
    return addresses.map((addr) => ({
      ...addr, // Preserve all original properties
      id: addr.id.toString(),
      name: `${addr.street_1 || addr.street_2 || 'Dirección'} - ${addr.municipality?.name || ''}`,
      fullAddress: [
        addr.street_1 || addr.street_2,
        addr.locality,
        addr.municipality?.name,
        addr.province?.name,
        addr.country?.name,
      ]
        .filter(Boolean)
        .join(', '),
    }))
  }, [addresses])

  // Compute delivery address municipality and province IDs
  const deliveryMunicipalityId = useMemo(() => {
    if (showNewAddressForm) {
      // For new address form, use the municipality from newAddressData
      const municipalityId = newAddressData.municipality?.id
      return municipalityId ? parseInt(municipalityId) : undefined
    } else if (selectedAddressId) {
      // For selected existing address, find the municipality ID
      const selectedAddress = clientAddresses.find(
        (addr) => addr.id === selectedAddressId
      )
      return selectedAddress?.municipality?.id
    }
    return undefined
  }, [
    showNewAddressForm,
    newAddressData.municipality?.id,
    selectedAddressId,
    clientAddresses,
  ])

  const deliveryProvinceId = useMemo(() => {
    if (showNewAddressForm) {
      // For new address form, use the province from newAddressData
      const provinceId = newAddressData.province?.id
      return provinceId ? parseInt(provinceId) : undefined
    } else if (selectedAddressId) {
      // For selected existing address, find the province ID
      const selectedAddress = clientAddresses.find(
        (addr) => addr.id === selectedAddressId
      )
      return selectedAddress?.province?.id
    }
    return undefined
  }, [
    showNewAddressForm,
    newAddressData.province?.id,
    selectedAddressId,
    clientAddresses,
  ])

  // Fetch business regions for delivery pricing
  const { regions, isLoadingRegions, isErrorRegions, errorRegions } =
    useBusinessRegionsQuery({
      params: {
        businessId: businessData?.id ?? 0,
        priceSystemId: formData.selectedPriceSystemId,
        municipalityId: deliveryMunicipalityId,
      },
      options: {
        enabled:
          deliveryType === 'BYREGION' &&
          !!formData.selectedPriceSystemId &&
          !!deliveryMunicipalityId &&
          !!deliveryProvinceId &&
          !!businessData?.id &&
          !isLoadingBusinessData &&
          !isErrorBusinessData,
      },
    })

  // Transform regions for select options
  const regionOptions = useMemo(() => {
    return regions.map((region) => ({
      value: region.id.toString(),
      label: region.name,
      description: region.description,
      price: region.price,
    }))
  }, [regions])

  const handleReceiverChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target

    // Use safe field error clearing
    clearFieldErrorByName(name)

    setReceiverInfo((prev) => ({ ...prev, [name]: value }))
  }

  const handleNewAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target

    // Use safe field error clearing
    clearFieldErrorByName(name)

    setNewAddressData((prev) => ({ ...prev, [name]: value }))
  }

  const handleNewAddressSelectChange = (
    name: keyof NewAddressData,
    value: string
  ) => {
    // Use safe field error clearing
    clearFieldErrorByName(name as string)

    setNewAddressData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'province' && {
        province: {
          id: value,
          name: provinceOptions.find((m: any) => m.value === value)?.label || '',
        },
        municipality: { id: '', name: '' },
      }),
      ...(name === 'municipality' && {
        municipality: {
          id: value,
          name: municipalityOptions.find((m: any) => m.value === value)?.label || '',
        },
      }),
    }))

    // Reset delivery region when province or municipality changes
    if (
      deliveryType === 'BYREGION' &&
      (name === 'province' || name === 'municipality')
    ) {
      setSelectedRegionId(undefined)
      setDeliveryCost('')
      clearValidationError('deliveryRegion')
    }
  }

  // Address interaction handlers
  const handleToggleNewAddressForm = () => {
    const nextState = !showNewAddressForm
    setShowNewAddressForm(nextState)
    clearValidationError('addressSelection')

    // Reset delivery region since address mode is changing
    if (deliveryType === 'BYREGION') {
      setSelectedRegionId(undefined)
      setDeliveryCost('')
      clearValidationError('deliveryRegion')
    }

    if (nextState) {
      // Showing form - clear selected address and load form data
      setSelectedAddressId(undefined)
      // For new clients with address data, preserve it, otherwise reset
      if (formData.isNewClient && formData.newAddressData) {
        setNewAddressData(formData.newAddressData)
      } else {
        setNewAddressData(defaultNewAddressData)
      }
    } else {
      // Hiding form - clear form data and auto-select first address if available
      setNewAddressData(defaultNewAddressData)
      if (clientAddresses.length > 0) {
        const defaultAddress =
          clientAddresses.find((addr) => addr.isMain) || clientAddresses[0]
        setSelectedAddressId(defaultAddress.id)
        setSelectedAddressFullAddress(defaultAddress.fullAddress)
      }
    }
  }

  useEffect(() => {
    if (deliveryType === 'BYREGION') {
      setSelectedRegionId(undefined)
      setDeliveryCost('')
      clearValidationError('deliveryRegion')
    }
  }, [deliveryMunicipalityId, deliveryType])

  const handleSelectExistingAddress = (addrId: string, fullAddress: string) => {
    clearValidationError('addressSelection')
    setSelectedAddressId(addrId)
    setSelectedAddressFullAddress(fullAddress)
    setShowNewAddressForm(false)
    setNewAddressData(defaultNewAddressData)

    // Reset delivery region since address changed
    if (deliveryType === 'BYREGION') {
      setSelectedRegionId(undefined)
      setDeliveryCost('')
      clearValidationError('deliveryRegion')
    }
  }

  const handleRegionChange = (regionId: string) => {
    clearValidationError('deliveryRegion')
    const region = regions.find((r) => r.id.toString() === regionId)
    setSelectedRegionId(parseInt(regionId))

    // Auto-set delivery cost from region price if available
    if (region) {
      setDeliveryCost(region.price.amount.toString())
      setDeliveryCostCurrency(region.price.codeCurrency)
    }
  }

  const handlePhoneChange = (phoneData: {
    number: string
    codeCountry: string
  }) => {
    clearValidationError('receiverPhone')
    setReceiverInfo((prev) => ({ ...prev, phone: phoneData }))
  }

  const handleDeliveryPersonChange = (personId: string) => {
    clearValidationError('deliveryPerson')
    setDeliveryPersonId(personId)
  }

  const handleDeliveryDateChange = (date: Date | undefined) => {
    clearValidationError('deliveryDate')
    setDeliveryDate(date)
  }

  const handleDeliveryCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearValidationError('deliveryCost')
    setDeliveryCost(e.target.value)
  }

  const handleImmediateDeliveryChange = (checked: boolean) => {
    setIsImmediateDelivery(checked)

    if (checked) {
      // Set current date/time for immediate delivery
      setDeliveryDate(new Date())
      // Clear any delivery date validation errors
      clearValidationError('deliveryDate')
    } else {
      // Reset to previous selected date or undefined for manual selection
      setDeliveryDate(formData.deliveryDate)
    }
  }

  // Create a function to get the current form data state
  const getCurrentFormData = (): Partial<BillingFormData> => {
    let deliveryData: Partial<BillingFormData> = {
      scheduleDelivery: true,
      receiverInfo,
      deliveryPersonId,
      deliveryDate: isImmediateDelivery ? new Date() : deliveryDate,
      deliveryCost: parseFloat(deliveryCost) || 0,
      deliveryCostCurrency,
      shippingRegionId: selectedRegionId,
      isImmediateDelivery,
    }

    if (selectedAddressId) {
      // User selected an existing address
      deliveryData.deliveryAddressId = selectedAddressId
      deliveryData.isNewAddress = false
      deliveryData.newAddressData = undefined
      deliveryData.deliveryAddressFullAddress = selectedAddressFullAddress
    } else if (showNewAddressForm) {
      // User is actively creating a new address
      deliveryData.isNewAddress = true
      deliveryData.newAddressData = newAddressData
      deliveryData.deliveryAddressId = undefined
      deliveryData.deliveryAddressFullAddress = undefined
    } else {
      // User hasn't made a choice yet or form is hidden
      deliveryData.isNewAddress = false
      deliveryData.newAddressData = undefined
      deliveryData.deliveryAddressId = undefined
      deliveryData.deliveryAddressFullAddress = undefined
    }

    return deliveryData
  }

  const handleNextClick = () => {
    // Validate form before proceeding
    if (!validateForm()) {
      console.warn('Form validation failed')
      return
    }

    const currentData = getCurrentFormData()
    onNext(currentData)
  }

  const handleBackClick = () => {
    const currentData = getCurrentFormData()
    if (onBack) {
      onBack(currentData)
    }
  }

  // Show error states if any data failed to load
  const hasErrors =
    isErrorDeliveryPersonnel ||
    isErrorProvinces ||
    isErrorMunicipalities ||
    isErrorAddresses ||
    isErrorBusinessConfigs ||
    isErrorRegions

  const isLoading =
    isLoadingDeliveryPersonnel ||
    isLoadingProvinces ||
    isLoadingMunicipalities ||
    isLoadingAddresses ||
    isLoadingBusinessConfigs ||
    isLoadingRegions

  // Error display component
  const ErrorText: FC<{ error?: string; className?: string }> = ({
    error,
    className,
  }) => {
    if (!error) return null
    return <p className={cn('text-sm text-red-500', className)}>{error}</p>
  }

  if (hasErrors && !isLoading) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error cargando datos necesarios para el envío.
            {isErrorDeliveryPersonnel && (
              <div>
                • Error cargando mensajeros: {errorDeliveryPersonnel?.message}
              </div>
            )}
            {isErrorProvinces && (
              <div>• Error cargando provincias: {errorProvinces?.message}</div>
            )}
            {isErrorMunicipalities && (
              <div>
                • Error cargando municipios: {errorMunicipalities?.message}
              </div>
            )}
            {isErrorAddresses && (
              <div>• Error cargando direcciones: {errorAddresses?.message}</div>
            )}
            {isErrorBusinessConfigs && (
              <div>• Error cargando configuración del negocio</div>
            )}
            {isErrorRegions && (
              <div>• Error cargando regiones: {errorRegions?.message}</div>
            )}
            Por favor, intenta recargar la página.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Loading state */}
      {isLoading && (
        <Alert>
          <AlertDescription>Cargando datos del envío...</AlertDescription>
        </Alert>
      )}

      {/* Receiver Details */}
      <h3 className="text-lg font-medium">Datos del Receptor</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-md border p-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="rec-name">Nombre (*)</Label>
          <Input
            id="rec-name"
            name="name"
            value={receiverInfo.name}
            onChange={handleReceiverChange}
            className={validationErrors.receiverName ? 'border-red-500' : ''}
            required
          />
          <ErrorText error={validationErrors.receiverName} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rec-lastName">Apellidos (*)</Label>
          <Input
            id="rec-lastName"
            name="lastName"
            value={receiverInfo.lastName}
            onChange={handleReceiverChange}
            className={
              validationErrors.receiverLastName ? 'border-red-500' : ''
            }
            required
          />
          <ErrorText error={validationErrors.receiverLastName} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rec-phone">Teléfono (*)</Label>
          <CountryPhoneSelector
            value={receiverInfo.phone}
            onChange={handlePhoneChange}
            placeholder="Número de teléfono"
            disabled={false}
            className={validationErrors.receiverPhone ? 'border-red-500' : ''}
          />
          <ErrorText error={validationErrors.receiverPhone} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rec-email">Correo electrónico</Label>
          <Input
            id="rec-email"
            name="email"
            type="email"
            value={receiverInfo.email || ''}
            onChange={handleReceiverChange}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="rec-notes">Añadir notas (receptor)</Label>
          <Textarea
            id="rec-notes"
            name="notes"
            value={receiverInfo.notes || ''}
            onChange={handleReceiverChange}
            placeholder="Instrucciones especiales, referencias, etc."
            rows={2}
          />
        </div>
      </div>

      <Separator />

      {/* Address Selection/Creation */}
      <div>
        <h3 className="text-lg font-medium">Dirección de Entrega</h3>

        {/* Show existing addresses section if there are addresses and form is not shown */}
        {clientAddresses.length > 0 && !showNewAddressForm && (
          <div className="mt-4 space-y-3">
            <Label>Seleccionar Dirección Guardada</Label>
            {isLoadingAddresses ? (
              <div className="text-sm text-muted-foreground">
                Cargando direcciones...
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {clientAddresses.map((addr) => (
                  <button
                    key={addr.id}
                    onClick={() =>
                      handleSelectExistingAddress(addr.id, addr.fullAddress)
                    }
                    className={cn(
                      'relative rounded-md border p-3 text-left transition-colors hover:border-primary',
                      selectedAddressId === addr.id
                        ? 'border-primary ring-1 ring-primary'
                        : 'border-input',
                      validationErrors.addressSelection ? 'border-red-500' : ''
                    )}
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{addr.name}</p>
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        {/* City/Locality, Municipality, Province */}
                        <p>
                          {[
                            addr.city,
                            addr.locality,
                            addr.municipality?.name,
                            addr.province?.name,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>

                        {/* Country and postal code */}
                        <p>
                          {[
                            addr.country?.name || 'Cuba',
                            addr.postalCode && `CP: ${addr.postalCode}`,
                          ]
                            .filter(Boolean)
                            .join(' - ')}
                        </p>
                      </div>
                    </div>
                    {addr.isMain && (
                      <span className="absolute right-2 top-2 rounded bg-primary px-1 py-0.5 text-xs text-primary-foreground">
                        Principal
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show "Add New Address" button for existing clients when form is not shown */}
        {formData.clientId && !formData.isNewClient && !showNewAddressForm && (
          <div className="mt-4 space-y-3">
            {clientAddresses.length === 0 && !isLoadingAddresses && (
              <p className="text-sm text-muted-foreground">
                Este proveedor no tiene direcciones guardadas.
              </p>
            )}
            <Button
              variant="outline"
              className="w-full gap-1.5"
              onClick={handleToggleNewAddressForm}
            >
              <PlusCircle className="h-4 w-4" />
              Añadir Nueva Dirección
            </Button>
          </div>
        )}

        {/* Show address form for new clients automatically */}
        {formData.isNewClient && !showNewAddressForm && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              {formData.newAddressData
                ? 'La dirección del proveedor ha sido cargada. Haga clic para editarla.'
                : 'Agregar dirección de entrega para el nuevo proveedor.'}
            </p>
            <Button
              variant="outline"
              className="w-full gap-1.5"
              onClick={handleToggleNewAddressForm}
            >
              <PlusCircle className="h-4 w-4" />
              {formData.newAddressData
                ? 'Editar Dirección de Entrega'
                : 'Añadir Dirección de Entrega'}
            </Button>
          </div>
        )}

        {/* Address validation error for selection */}
        {!showNewAddressForm && (
          <ErrorText
            error={validationErrors.addressSelection}
            className="mt-2"
          />
        )}

        {showNewAddressForm && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Nueva Dirección</h4>
              {clientAddresses.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleToggleNewAddressForm}
                >
                  Seleccionar Existente
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-md border p-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="del-mainAddress">Calle Principal (*)</Label>
                <Input
                  id="del-mainAddress"
                  name="mainAddress"
                  value={newAddressData.mainAddress || ''}
                  onChange={handleNewAddressInputChange}
                  className={
                    validationErrors.newAddressMainAddress
                      ? 'border-red-500'
                      : ''
                  }
                  required
                />
                <ErrorText error={validationErrors.newAddressMainAddress} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="del-secondaryAddress">Calle Secundaria</Label>
                <Input
                  id="del-secondaryAddress"
                  name="secondaryAddress"
                  value={newAddressData.secondaryAddress || ''}
                  onChange={handleNewAddressInputChange}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="del-locality">Localidad</Label>
                <Input
                  id="del-locality"
                  name="locality"
                  value={newAddressData.locality || ''}
                  onChange={handleNewAddressInputChange}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="country">País (*)</Label>
                <div className="flex">
                  <CountrySelector
                    value={newAddressData.country?.name || 'Cuba'}
                    onChange={(country: Country) => {
                      clearValidationError('newAddressCountry')
                      setNewAddressData({
                        ...newAddressData,
                        country: {
                          id: country.id.toString(),
                          name: country.name,
                        },
                        province: {
                          id: '',
                          name: '',
                        },
                        municipality: {
                          id: '',
                          name: '',
                        },
                      })
                    }}
                    valueType="name"
                    showFlag={true}
                    showPrefix={false}
                    placeholder="Seleccionar país"
                    disabled={false}
                    className={cn(
                      'flex-1',
                      validationErrors.newAddressCountry ? 'border-red-500' : ''
                    )}
                  />
                </div>
                <ErrorText error={validationErrors.newAddressCountry} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="del-province">Provincia (*)</Label>
                <Select
                  name="province"
                  value={newAddressData.province?.id?.trim() ?? undefined}
                  onValueChange={(value) =>
                    handleNewAddressSelectChange('province', value)
                  }
                  disabled={isLoadingProvinces}
                >
                  <SelectTrigger
                    id="del-province"
                    className={
                      validationErrors.newAddressProvince
                        ? 'border-red-500'
                        : ''
                    }
                  >
                    <SelectValue
                      placeholder={
                        isLoadingProvinces
                          ? 'Cargando...'
                          : 'Seleccionar provincia'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {provinceOptions.map(
                      (opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <ErrorText error={validationErrors.newAddressProvince} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="del-municipality">Municipio (*)</Label>
                <Select
                  name="municipality"
                  value={newAddressData.municipality?.id?.trim() ?? undefined}
                  onValueChange={(value) =>
                    handleNewAddressSelectChange('municipality', value)
                  }
                  disabled={
                    !newAddressData.province?.id?.trim() ||
                    isLoadingMunicipalities
                  }
                >
                  <SelectTrigger
                    id="del-municipality"
                    className={
                      validationErrors.newAddressMunicipality
                        ? 'border-red-500'
                        : ''
                    }
                  >
                    <SelectValue
                      placeholder={
                        !newAddressData.province?.id?.trim()
                          ? 'Selecciona provincia'
                          : isLoadingMunicipalities
                            ? 'Cargando...'
                            : 'Seleccionar municipio'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {municipalityOptions.map(
                      (opt: { value: string; label: string }) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <ErrorText error={validationErrors.newAddressMunicipality} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="del-postalCode">Código postal</Label>
                <Input
                  id="del-postalCode"
                  name="postalCode"
                  value={newAddressData.postalCode || ''}
                  onChange={handleNewAddressInputChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Delivery Scheduling Details */}
      <h3 className="text-lg font-medium">Detalles del Envío</h3>

      {/* Immediate Delivery Checkbox */}
      <div className="flex items-center space-x-2 rounded-md border p-4">
        <Checkbox
          id="immediate-delivery"
          checked={isImmediateDelivery}
          onCheckedChange={handleImmediateDeliveryChange}
        />
        <Label htmlFor="immediate-delivery" className="cursor-pointer">
          Entrega inmediata (usar fecha y hora actual)
        </Label>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-md border p-4 md:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="deliveryPerson">Mensajero (*)</Label>
          <Select
            value={deliveryPersonId}
            onValueChange={handleDeliveryPersonChange}
            disabled={isLoadingDeliveryPersonnel}
          >
            <SelectTrigger
              id="deliveryPerson"
              className={
                validationErrors.deliveryPerson ? 'border-red-500' : ''
              }
            >
              <SelectValue
                placeholder={
                  isLoadingDeliveryPersonnel
                    ? 'Cargando...'
                    : 'Seleccionar mensajero'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {deliveryPeople && deliveryPeople.length > 0 ? (
                deliveryPeople.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    className="cursor-pointer"
                  >
                    {p.name}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-delivery-people" disabled>
                  No hay mensajeros disponibles
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <ErrorText error={validationErrors.deliveryPerson} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="deliveryDate">
            Fecha y Hora de Envío {!isImmediateDelivery && '(*)'}
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="deliveryDate"
                variant={'outline'}
                disabled={isImmediateDelivery}
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !deliveryDate &&
                    !isImmediateDelivery &&
                    'text-muted-foreground',
                  isImmediateDelivery && 'cursor-not-allowed opacity-50',
                  validationErrors.deliveryDate ? 'border-red-500' : ''
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {isImmediateDelivery ? (
                  <span>
                    {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}
                  </span>
                ) : deliveryDate ? (
                  format(deliveryDate, 'dd/MM/yyyy HH:mm', { locale: es })
                ) : (
                  <span>Fecha y hora</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={deliveryDate}
                onSelect={handleDeliveryDateChange}
                initialFocus
              />
              <div className="border-t border-border p-3">
                <Label htmlFor="deliveryTime" className="text-sm">
                  Hora
                </Label>
                <Input
                  id="deliveryTime"
                  type="time"
                  className="mt-1"
                  step="60"
                  value={
                    deliveryDate
                      ? format(deliveryDate, 'HH:mm', { locale: es })
                      : ''
                  }
                  onChange={(e) => {
                    const time = e.target.value.split(':')
                    setDeliveryDate((currentDate) => {
                      const newDate = currentDate
                        ? new Date(currentDate)
                        : new Date()
                      if (!currentDate) {
                        const today = new Date()
                        newDate.setFullYear(
                          today.getFullYear(),
                          today.getMonth(),
                          today.getDate()
                        )
                      }
                      newDate.setHours(
                        parseInt(time[0] || '0'),
                        parseInt(time[1] || '0'),
                        0
                      )
                      return newDate
                    })
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
          {!isImmediateDelivery && (
            <ErrorText error={validationErrors.deliveryDate} />
          )}
          {isImmediateDelivery && (
            <p className="text-sm text-muted-foreground">
              Se usará la fecha y hora actual para la entrega
            </p>
          )}
        </div>

        {/* Delivery Pricing Logic */}
        {deliveryType === 'BYREGION' ? (
          <div className="space-y-1">
            <Label htmlFor="deliveryRegion">Región de Envío (*)</Label>
            <Select
              value={selectedRegionId?.toString()}
              onValueChange={handleRegionChange}
              disabled={isLoadingRegions || !deliveryMunicipalityId}
            >
              <SelectTrigger
                id="deliveryRegion"
                className={
                  validationErrors.deliveryRegion ? 'border-red-500' : ''
                }
              >
                {selectedRegionId ? (
                  <div className="flex w-full items-center gap-2">
                    <span className="truncate">
                      {
                        regionOptions.find(
                          (r) => r.value === selectedRegionId.toString()
                        )?.label
                      }
                    </span>
                    <span className="flex-shrink-0 text-sm font-medium">
                      {regionOptions
                        .find((r) => r.value === selectedRegionId.toString())
                        ?.price.amount.toFixed(2)}{' '}
                      {
                        regionOptions.find(
                          (r) => r.value === selectedRegionId.toString()
                        )?.price.codeCurrency
                      }
                    </span>
                  </div>
                ) : (
                  <SelectValue
                    placeholder={
                      !deliveryMunicipalityId
                        ? 'Primero selecciona o crea una dirección'
                        : isLoadingRegions
                          ? 'Cargando regiones...'
                          : 'Seleccionar región'
                    }
                  />
                )}
              </SelectTrigger>
              <SelectContent>
                {regionOptions.length > 0 ? (
                  regionOptions.map((region) => (
                    <SelectItem
                      key={region.value}
                      value={region.value}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span>{region.label}</span>
                        {region.description && (
                          <span className="text-xs text-muted-foreground">
                            {region.description}
                          </span>
                        )}
                        <span className="text-xs font-medium">
                          {region.price.amount.toFixed(2)}{' '}
                          {region.price.codeCurrency}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-regions" disabled>
                    No hay regiones disponibles
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {!deliveryMunicipalityId ? (
              <p className="text-sm text-muted-foreground">
                Las regiones de envío se cargarán una vez que selecciones o
                completes la dirección de entrega
              </p>
            ) : (
              <ErrorText error={validationErrors.deliveryRegion} />
            )}
          </div>
        ) : deliveryType === 'VARIABLE' ? (
          <div className="space-y-1">
            <Label htmlFor="deliveryCost">Precio de envío</Label>
            <div className="flex gap-2">
              <Input
                id="deliveryCost"
                type="number"
                min="0"
                step="0.01"
                value={deliveryCost}
                onChange={handleDeliveryCostChange}
                placeholder="0.00"
                className={cn(
                  'flex-1',
                  validationErrors.deliveryCost ? 'border-red-500' : ''
                )}
              />
              <Select
                value={deliveryCostCurrency}
                onValueChange={setDeliveryCostCurrency}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD" className="cursor-pointer">
                    USD
                  </SelectItem>
                  <SelectItem value="CUP" className="cursor-pointer">
                    CUP
                  </SelectItem>
                  <SelectItem value="EUR" className="cursor-pointer">
                    EUR
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ErrorText error={validationErrors.deliveryCost} />
          </div>
        ) : null}
      </div>

      {/* Show pricing info for selected region */}
      {deliveryType === 'BYREGION' && selectedRegionId && (
        <div className="rounded-md bg-muted p-3">
          <div className="text-sm">
            <div className="flex justify-between">
              <span>Precio de envío:</span>
              <span className="font-medium">
                {deliveryCost} {deliveryCostCurrency}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Hidden button to trigger onNext from wizard footer */}
      <button
        onClick={handleNextClick}
        className="hidden"
        id="delivery-details-next"
        disabled={isLoading || hasValidationErrors}
      ></button>

      {/* Hidden button to trigger onBack from wizard footer */}
      <button
        onClick={handleBackClick}
        className="hidden"
        id="delivery-details-back"
      ></button>
    </div>
  )
}

export default DeliveryDetailsStep
